import {
  Flex,
  Icon,
  Input,
  Select,
  Stack,
  HStack,
  Text,
  Radio,
  Checkbox,
  Switch,
  Menu,
  MenuList,
  MenuItem,
  MenuOptionGroup,
  MenuItemOption,
  MenuButton,
  Button,
  UnorderedList,
  ListItem,
  Divider
} from '@chakra-ui/react';
import { chakraComponents } from 'chakra-react-select';
import { useState } from 'react';
import { map } from 'lodash-es';

import { usePeopleOptions } from '@services/query-services/people.query-service';
import { useAvailableSymbols } from '@services/query-services/fmp.query-service';
import { ReskinMeetingCardContext } from '@components/general/card/reskin-meeting-card/reskin-meeting-card.component';
import { useAllMeetingContentById } from '@services/query-services/meetings.query-service';
import { useDebouncedProp } from '@util/hooks';
import { SFSquareArrowDown } from '@assets/icons';
import { DURATION_OPTIONS, VISIBILITY_OPTIONS } from '@util/constants';

import CustomSelect from '@components/general/form/custom-select.component';
import FormInput from '@components/general/form/form-input.component';
import StyledSelect from '@components/general/styled-select.component';
import StyledSwitch from '@components/general/styled-switch.component';
import ReskinMeetingMenu from '@components/general/card/reskin-meeting-card/reskin-meeting-menu.component';

const fakeMeetingId =
  'f7943f5426875095bfc49f8bd15b626c7577aa0a_0ph88m8tm7iv7o3bjl6po568e2_20250407T133000Z';

export default function FormFieldsSection() {
  const { data: peopleOptions } = usePeopleOptions();
  const [selectedPeople, setSelectedPeople] = useState([]);
  const [selectedPrivacy, setSelectedPrivacy] = useState();
  const [inputText, setInputText] = useState('');
  const [selectedTickers, setSelectedTickers] = useState([]);
  const [debouncedInputText] = useDebouncedProp(inputText);
  const { data: allMeetingData } = useAllMeetingContentById(fakeMeetingId, false, {
    placeholderData: { value: null, card: null, bot: null }
  });
  const { value: meeting, card, bot } = allMeetingData;

  const { data: availableSymbols, isLoading } = useAvailableSymbols(debouncedInputText, {
    enabled: debouncedInputText?.length >= 2 && debouncedInputText?.length <= 30,
    select: (symbols) => map(symbols, 'value')
  });

  const handleInputChange = (inputText, meta) => {
    if (meta.action !== 'input-blur' && meta.action !== 'menu-close') {
      setInputText(inputText);
    }
  };

  return (
    <Flex
      direction='row'
      wrap='wrap'
      gap={12}
    >
      <Stack>
        <Text fontWeight={600}>Input</Text>
        <Input
          placeholder='sm'
          size='sm'
        />
        <FormInput
          size='sm'
          label='Form Input (custom sm)'
          errorMsg='Please enter something.'
        />
        <Input
          placeholder='md'
          size='md'
        />
        <FormInput
          size='md'
          label='Form Input (custom md)'
          errorMsg='Please enter something.'
        />
        <Input
          placeholder='lg'
          size='lg'
        />
        <FormInput
          size='lg'
          label='Form Input (custom lg)'
          errorMsg='Please enter something.'
        />
      </Stack>
      <Stack w='400px'>
        <Text
          fontWeight={600}
          mb={2}
        >
          Select/Dropdown
        </Text>
        <Divider mb={6} />
        <Stack>
          <Text fontWeight={500}>Custom Select</Text>
          <Text fontSize='xs'>
            Should be the default Select component moving forward in almost all cases.
          </Text>

          <CustomSelect
            selectProps={{ width: '100%' }}
            placeholder='CustomSelect Example 1 sm'
            getOptionLabel={(opt) => opt?.label}
            getOptionValue={(opt) => opt?.value}
            size='sm'
            value={selectedPeople}
            onChange={setSelectedPeople}
            options={peopleOptions}
          />
          <CustomSelect
            selectProps={{ width: '100%' }}
            placeholder='CustomSelect Example 1 md'
            getOptionLabel={(opt) => opt?.label}
            getOptionValue={(opt) => opt?.value}
            size='md'
            value={selectedPeople}
            onChange={setSelectedPeople}
            options={peopleOptions}
          />
          <CustomSelect
            selectProps={{ width: '100%' }}
            placeholder='CustomSelect Example 1 lg'
            getOptionLabel={(opt) => opt?.label}
            getOptionValue={(opt) => opt?.value}
            value={selectedPeople}
            onChange={setSelectedPeople}
            options={peopleOptions}
          />
          <CustomSelect
            placeholder='CustomSelect Example 2 lg'
            getOptionValue={(opt) => opt?.symbol}
            inputValue={inputText}
            onInputChange={handleInputChange}
            options={availableSymbols}
            value={selectedTickers}
            onChange={setSelectedTickers}
            filterOption={null}
            isLoading={!!inputText && isLoading}
            controlProps={{ py: 3 }}
            components={{
              Option,
              MultiValueLabel,
              LoadingMessage: (props) => (
                <LoadingMessage
                  inputHasTwoChars={inputText?.length >= 2}
                  {...props}
                />
              ),
              NoOptionsMessage: (props) => (
                <NoOptionsMessage
                  hasInput={inputText?.length > 0}
                  {...props}
                />
              )
            }}
          />
        </Stack>
        <Divider my={6} />
        <Stack>
          <Text fontWeight={500}>Chakra Default Select</Text>
          <Text fontSize='xs'>Used only in a few older situations</Text>
          <UnorderedList
            mb={2}
            fontSize='xs'
          >
            <ListItem>Create Lens Form - Duration</ListItem>
            <ListItem>Create Meeting Modal - Category (Old?)</ListItem>
            <ListItem>Manual Digest Page - For Slack verification, can remove?</ListItem>
            <ListItem>Settings - User Settings - Timezone</ListItem>
            <ListItem>Manual Digest Page - For Slack verification, can remove?</ListItem>
          </UnorderedList>
          <Select
            placeholder='Default Chakra Select (outline)'
            variant='reskinOutline'
          >
            <FakeOptions />
          </Select>
          <Select
            placeholder='Default Chakra Select (filled)'
            variant='reskinFilled'
          >
            <FakeOptions />
          </Select>
        </Stack>
        <Divider my={6} />
        <Stack>
          <Text fontWeight={500}>Styled Select</Text>
          <Text fontSize='xs'>
            Only really used on the Create Lens form, Person Meeting Timeline, and Create User Page.{' '}
          </Text>
          <StyledSelect
            selectLabel='StyledSelect'
            placeholder='Custom Chakra Menu Component'
            options={VISIBILITY_OPTIONS.map((visOption) => ({
              ...visOption,
              icon: (
                <Icon
                  as={visOption.icon}
                  boxSize={4}
                />
              )
            }))}
            value={selectedPrivacy}
            onChange={setSelectedPrivacy}
            menuButtonProps={{
              backgroundColor: 'surface.containerLow'
            }}
          />
        </Stack>
      </Stack>
      <Stack>
        <Text fontWeight={600}>Radio</Text>
        <Radio size='xs'>XS Radio</Radio>
        <Radio
          size='sm'
          variant='secondary'
          isChecked
        >
          SM Radio
        </Radio>
        <Radio
          size='md'
          isChecked
        >
          MD Radio
        </Radio>
        <Radio
          size='lg'
          isChecked
        >
          LG Radio
        </Radio>{' '}
        <Radio
          size='xl'
          isChecked
        >
          XL Radio
        </Radio>
      </Stack>
      <Stack>
        <Text fontWeight={600}>Checkbox</Text>
        <Checkbox size='sm'>SM Checkbox</Checkbox>
        <Checkbox size='md'>MD Checkbox</Checkbox>
        <Checkbox size='lg'>LG Checkbox</Checkbox>
      </Stack>
      <Stack>
        <Text fontWeight={600}>Switch</Text>
        <Switch size='sm'>SM Switch</Switch>
        <Switch size='md'>MD Switch</Switch>
        <Switch size='lg'>LG Switch</Switch>
        <StyledSwitch />
      </Stack>
      <Stack>
        <Text fontWeight={600}>Menu</Text>
        <FakeMenu title='Base Variant' />
        <FakeMenu
          title='Select Variant'
          variant='select'
        />
        <StyledSelect
          selectLabel='StyledSelectMenu'
          placeholder='Technically a Menu Componen, Not Select '
          options={VISIBILITY_OPTIONS.map((visOption) => ({
            ...visOption,
            icon: (
              <Icon
                as={visOption.icon}
                boxSize={4}
              />
            )
          }))}
          value={selectedPrivacy}
          onChange={setSelectedPrivacy}
          menuButtonProps={{
            backgroundColor: 'surface.containerLow'
          }}
        />
        <Flex
          gap={3}
          align='center'
        >
          <Text>Meeting Menu (Scrum Example)</Text>
          <ReskinMeetingCardContext.Provider
            value={{
              meeting,
              bot,
              card,
              showCardActions: true,
              onCardActionClick: () => console.log('hey'),
              onMeetingActionClick: () => console.log('hey')
            }}
          >
            <ReskinMeetingMenu showViewTranscriptButton />
          </ReskinMeetingCardContext.Provider>
        </Flex>
      </Stack>
    </Flex>
  );
}

function FakeMenu({ title, variant }) {
  return (
    <Menu variant={variant}>
      <MenuButton
        as={Button}
        rightIcon={<SFSquareArrowDown />}
      >
        {title}
      </MenuButton>
      <MenuList>
        <MenuItem>Fake Item 1</MenuItem>
        <MenuItem>Fake Item 2 Is This One</MenuItem>
        <MenuItem>Fake Item 3 of Regular List</MenuItem>
        <MenuOptionGroup title='Fake Option Group'>
          <MenuItemOption>Fake Option Item 1</MenuItemOption>
          <MenuItemOption>Fake Option Item 2</MenuItemOption>
          <MenuItemOption>Fake Option Item 3</MenuItemOption>
        </MenuOptionGroup>
      </MenuList>
    </Menu>
  );
}

function FakeOptions() {
  return (
    <>
      {DURATION_OPTIONS.map(({ value, label }) => (
        <option
          key={value}
          value={value}
        >
          {label}
        </option>
      ))}
    </>
  );
}

function MultiValueLabel({ data: option, ...props }) {
  return (
    <chakraComponents.MultiValueLabel {...props}>
      <Text
        as='span'
        color='surface.onSurface'
        variant='label-500-md'
      >
        {option?.symbol}
      </Text>
    </chakraComponents.MultiValueLabel>
  );
}

function Option({ data: option, ...props }) {
  return (
    <chakraComponents.Option {...props}>
      <HStack>
        <Text
          as='span'
          color='surface.onSurface'
          variant='label-500-md'
        >
          {option?.symbol}
        </Text>
        <Text
          as='span'
          color='surface.outline'
          variant='label-500-md'
        >
          {option?.companyName}
        </Text>
      </HStack>
    </chakraComponents.Option>
  );
}

function LoadingMessage({ inputHasTwoChars, ...props }) {
  return (
    <chakraComponents.LoadingMessage {...props}>
      <Text>{inputHasTwoChars ? 'Loading...' : 'Input at least 2 characters to search...'}</Text>
    </chakraComponents.LoadingMessage>
  );
}

function NoOptionsMessage({ hasInput, ...props }) {
  return (
    <chakraComponents.NoOptionsMessage {...props}>
      <Text>{hasInput ? 'No tickers found' : 'Input a ticker or company name'}</Text>
    </chakraComponents.NoOptionsMessage>
  );
}
